import React, { ReactElement, useEffect, useState } from "react";
import { MdOutlineMarkChatUnread } from "react-icons/md";
import TimeAgo from "react-timeago";
import {
    TicketView as ApiTicketView,
    PortalEventType, PortalMessageEvent, SenderType, TicketStatus, useGetTicketsQuery,
} from "../../__generated__/api";
import { Loading } from "../../components";
import Avatar from "../../components/avatar/Avatar";
import TicketView from "../../components/ticket-view/TicketView";
import useCreateTicket from "../../contexts/CreateTicketContext";
import useNotifications from "../../contexts/NotificationContext";
import useEvents from "../../contexts/PortalSubscriptionContext";
import useRouting, { Link } from "../../contexts/Routing";

type Ticket = Exclude<ReturnType<(typeof useGetTicketsQuery)>["data"], undefined>["tickets"][0];

function TicketRow({ ticket, unreads }: {ticket: Ticket, unreads: Set<string>}) {
    const { Paths } = useRouting();

    const lastResponseDate = ticket.lastResponseDate
        ? new Date(ticket.lastResponseDate)
        : new Date(ticket.createdDate);
    const statusClass = ticket.status === TicketStatus.Closed || TicketStatus.Ignored ? "list-border-primary" : "";

    return (
        <Link
            to={Paths.TICKETS_VIEW}
            params={{
                ticketId: ticket.ticketNumber ?? ticket.id,
            }}
            className={`d-flex list-group-item list-group-item-action rounded shadow
                        border-top-0 border-end-0 list-border ${statusClass} my-2`}
        >

            <div className="col">
                <h5 className="text-start">
                    {unreads.has(ticket.chatId) ? (
                        <span className="p-1 ms-n1 bg-danger badge badge-pill rounded me-2 shadow" title="Unread messages">
                            <MdOutlineMarkChatUnread />
                            <span className="visually-hidden">New unreads</span>
                        </span>
                    ) : (
                        <span className="ms-4" />
                    )}
                    <small className="text-light me-3">
                        #
                        {ticket.ticketNumber}
                    </small>
                    {ticket.subject}
                </h5>
                <small className="text-start">
                    {ticket.description}
                </small>
            </div>

            <div className="col-2 text-light">
                {ticket.assignee && (
                    <span>
                        <Avatar src={ticket.assignee?.avatarUrl as string | undefined} variant="xxs" />
                        {ticket.assignee?.name}
                    </span>
                )}
            </div>

            <div className="col-1 text-start text-light">
                <TimeAgo
                    date={lastResponseDate}
                    minPeriod={60}
                    title={`${lastResponseDate.toLocaleDateString()}
                                                ${lastResponseDate.toLocaleTimeString()}`}
                />
            </div>
        </Link>
    );
}

function ColumnHeaders({ title }: {title: string}): ReactElement {
    return (
        <div className="d-flex text-start text-light w-100 px-4 px-1 mb-n2 mt-3 align-items-end">
            <h4 className="col text-gray-500 ms-1 my-3 py-1">
                {title}
            </h4>

            <small className="col-2">
                Assignee
            </small>

            <small className="col-1">
                Last Updated
            </small>
        </div>
    );
}

function TicketsDisplay(): ReactElement {
    const {
        Paths, getSearchParams, historyPushSearch,
    } = useRouting();

    const { requestTypeId } = getSearchParams(Paths.TICKETS);
    const createTicketActions = useCreateTicket();
    const {
        data, loading: isLoading, refetch,
    } = useGetTicketsQuery({
        variables: {
            filter: ApiTicketView.CreatedTickets,
            filters: {
                limit: 50,
            },
        },
    });

    createTicketActions.onChangeRequestType(id => {
        if (id) {
            historyPushSearch({ requestTypeId: id });
        } else {
            historyPushSearch({});
        }
    });

    const [unreads, setUnreads] = useState(new Set<string>());

    const [currentChatId, setCurrentChatId] = useState<string>();
    const notifications = useNotifications();

    const addUnread = (chatId: string) => {
        setUnreads(current => {
            const copy = Array.from(current);
            copy.push(chatId);
            return new Set(copy);
        });
    };

    const removeUnread = (chatId: string) => {
        setUnreads(current => {
            const copy = new Set(Array.from(current));
            copy.delete(chatId);
            return copy;
        });
    };

    useEvents(api => {
        api.on(PortalEventType.MessageNew, 10, (event: PortalMessageEvent) => {
            if (event.sender?.type === SenderType.User) {
                return;
            }

            if (currentChatId !== event.chatId) {
                addUnread(event.chatId);
            }

            notifications.message(
                event.messageId as string,
                `New Message from ${event.sender?.name}`,
                event.message.plaintext?.substring(0, 100) as string,
            );
        });
        api.on(PortalEventType.Resync, 0, async () => {
            await refetch();
        });
    }, [currentChatId]);

    const createTicket = useCreateTicket();
    const tickets = data?.tickets.sortByDescending(ticket => new Date(ticket.lastResponseDate
        ? ticket.lastResponseDate
        : ticket.createdDate).getTime());
    const openTickets = tickets?.filter(ticket => ticket.status !== TicketStatus.Ignored
        && ticket.status !== TicketStatus.Closed);
    const closedTickets = tickets?.filter(ticket => ticket.status === TicketStatus.Ignored
        || ticket.status === TicketStatus.Closed);

    useEffect(() => {
        if (data?.tickets) {
            const ids = data?.tickets.filterMap(row => row.chatId, row => row.hasUnreadByCurrentUser ?? false) ?? [];
            setUnreads(new Set(ids));
        }
    }, [data?.tickets]);

    if (requestTypeId) {
        createTicketActions.show(requestTypeId);
    }
    if (isLoading) {
        return <Loading />;
    }
    return (
        <div className="">
            {tickets?.isEmpty && (
                <div className="
                position-fixed d-flex flex-column container-sm top-50 start-50 translate-middle border p-3 rounded bg-white"
                >
                    <p className="text-center">
                        🤔 Seems like you don&apos;t have any tickets now. Would you like to create a new request?
                    </p>
                    <button
                        type="button"
                        className="btn btn-primary mb-2  align-self-center"
                        onClick={() => createTicket.show()}
                    >
                        New Request
                    </button>
                </div>
            )}
            {tickets?.isNotEmpty && (
                <div className="m-3 mb-n4 d-flex justify-content-end">
                    <button type="button" className="btn btn-primary" onClick={() => createTicket.show()}>
                        New Request
                    </button>
                </div>
            )}
            <div>
                {openTickets?.isNotEmpty && (
                    <div>
                        <ColumnHeaders title="Your open tickets" />
                        <div className="list-group list-group-flush mt-2 ms-2 me-2">
                            {openTickets?.map(ticket => <TicketRow key={ticket.id} ticket={ticket} unreads={unreads} />)}
                        </div>
                    </div>
                )}
                {closedTickets?.isNotEmpty && (
                    <div>

                        <ColumnHeaders title="Closed Tickets" />
                        <div className="list-group list-group-flush mt-2 ms-2 me-2">

                            {closedTickets?.map(ticket => <TicketRow key={ticket.id} ticket={ticket} unreads={unreads} />)}
                        </div>
                    </div>
                )}

            </div>
            <TicketView
                onDisplay={chatId => {
                    removeUnread(chatId);
                    setCurrentChatId(chatId);
                }}
                onHide={() => setCurrentChatId(undefined)}
            />
        </div>
    );
}

export default function Tickets(): ReactElement {
    return (
        <TicketsDisplay />
    );
}
